import Router from 'public/components/Router';
import PublicRoute from 'enums/publicRoutes';
import { Layout } from 'antd';
import { StyledPage } from './style';
import PageContent from './PageContent';
import PageLayout from './PageLayout';
import useQueryApiClient from 'utils/useQueryApiClient';
import DefaultSecondHeader from './DefaultSecondHeader';
import Banner from './Banner';
import { useBannerDispatch, useProductDispatch } from '../../context';
import { Spinner } from 'ui';
import { useCategoryDispatch } from '../../context';
import DefaultFooter from './DefaultFooter/DefaultFooter';
import MobileHeader from '../MobileLayout/MobileHeader/MobileHeader';
import MobileMenu from '../MobileLayout/MobileMenu/MobileMenu';
import CookieConsent from './CookieConsent';
import {
  useCartState,
  CartActionType,
  useCartDispatch,
  CART_RESTORE_PENDING_KEY,
  readPaymentInProgress,
} from 'public/context/CartContext';
import { useEffect, useRef } from 'react';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import useMarketType from '../../../utils/useMarketType';
import { useAuth } from '../../../hooks/useAuth';
import HomeInfoBar from './HomeInfoBar';

const { Content } = Layout;

const DefaultLayout = () => {
  const dispatchBanners = useBannerDispatch();
  const dispatchProducts = useProductDispatch();
  const dispatchCategory = useCategoryDispatch();
  const dispatchCart = useCartDispatch();
  const { marketType } = useMarketType();
  const { marketType: cartMarketType } = useCartState();
  const { user } = useAuth();

  useEffect(() => {
    if (cartMarketType && marketType !== cartMarketType) {
      if (marketType === 'WHOLESALE' && !user) {
        return;
      }

      dispatchCart({
        type: CartActionType.SWITCH_MARKET,
        marketType: marketType as 'EMARKET' | 'WHOLESALE',
      });

      appendData({ type: marketType });
    }
  }, [marketType, cartMarketType]);

  const { appendData } = useQueryApiClient({
    request: {
      url: `api/v2/cart/latest`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      if (response?.cartId) {
        dispatchCart({
          type: CartActionType.REFETCH_CART_PRODUCTS,
          farmProducts: response.farmProducts,
          priceTotal: response.priceTotal,
          requiresAuth: response.requires_auth,
          cartId: response.cartId,
          cartName: response.cartName,
          marketType: marketType,
        });
      } else {
        dispatchCart({
          type: CartActionType.CLEAR_CART,
        });
      }
    },
  });

  const { refetch: refetchBanners, isLoading: isLoadingBanners } =
    useQueryApiClient({
      request: {
        url: 'api/v2/banners',
      },
      onSuccess: (response) => {
        dispatchBanners({
          type: 'SAVE_PAYLOAD',
          payload: { banners: response, refetch: refetchBanners },
          isLoading: isLoadingBanners,
        });
      },
    });

  const { refetch: refetchProducts, isLoading: isLoadingProducts } =
    useQueryApiClient({
      request: {
        url: 'api/v2/products/popular',
      },
      onSuccess: (response) => {
        dispatchProducts({
          type: 'SAVE_PAYLOAD',
          payload: { products: response, refetch: refetchProducts },
          isLoading: isLoadingProducts,
        });
      },
    });

  const {
    refetch: refetchProductsDiscount,
    isLoading: isLoadingProductsDiscount,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/products/discounted',
    },
    onSuccess: (response) => {
      dispatchProducts({
        type: 'SAVE_PAYLOAD',
        payload: {
          productsDiscounted: response,
          refetch: refetchProductsDiscount,
        },
        isLoading: isLoadingProductsDiscount,
      });
    },
  });

  const { refetch: refetchCategory, isLoading: isLoadingCategory } =
    useQueryApiClient({
      request: {
        url: 'api/v2/categories/menu/desktop',
      },
      onSuccess: (response) => {
        dispatchCategory({
          type: 'SAVE_PAYLOAD',
          payload: { category: response, refetch: refetchCategory },
          isLoading: isLoadingCategory,
        });
      },
    });

  const {
    refetch: refetchPopularCategories,
    isLoading: isLoadingPopularCategories,
  } = useQueryApiClient({
    request: {
      url: 'api/v2/categories/popular',
    },
    onSuccess: (response) => {
      dispatchCategory({
        type: 'SAVE_PAYLOAD',
        payload: {
          popularCategory: response,
          refetch: refetchPopularCategories,
        },
        isLoading: isLoadingPopularCategories,
      });
    },
  });

  const { refetch: refetchProductsCount, isLoading: isLoadingProductsCount } =
    useQueryApiClient({
      request: {
        url: 'api/v2/products/get/count',
      },
      onSuccess: (response) => {
        dispatchProducts({
          type: 'SAVE_PAYLOAD',
          payload: {
            productsCounted: response,
            refetch: refetchProductsCount,
          },
          isLoading: isLoadingProductsCount,
        });
      },
    });

  const { farmProducts, cartId } = useCartState();
  const intl = useIntl();
  const [notificationApi, notificationHolder] = notification.useNotification();

  const { appendData: restoreSavedCart } = useQueryApiClient({
    request: {
      url: `api/v2/cart/latest`,
      disableOnMount: true,
    },
    onSuccess: (response) => {
      if (!response?.cartId || !response.farmProducts?.length) {
        return;
      }

      dispatchCart({
        type: CartActionType.REFETCH_CART_PRODUCTS,
        farmProducts: response.farmProducts,
        priceTotal: response.priceTotal,
        requiresAuth: response.requires_auth,
        cartId: response.cartId,
        cartName: response.cartName,
        marketType: 'EMARKET',
      });

      if (response.cartUpdated) {
        notificationApi.warning({
          message: intl.formatMessage({ id: 'cart.restored_changed_message' }),
          description: intl.formatMessage({
            id: 'cart.restored_changed_description',
          }),
          showProgress: true,
          pauseOnHover: true,
          duration: 10,
        });
      }
    },
  });

  useEffect(() => {
    if (
      !user?.authenticated ||
      sessionStorage.getItem(CART_RESTORE_PENDING_KEY) !== '1'
    ) {
      return;
    }

    sessionStorage.removeItem(CART_RESTORE_PENDING_KEY);

    if (
      marketType !== 'EMARKET' ||
      farmProducts.length > 0 ||
      readPaymentInProgress()
    ) {
      return;
    }

    restoreSavedCart({ type: 'EMARKET' });
  }, [user?.authenticated]);

  const { appendData: syncEmptiedCart } = useQueryApiClient({
    request: {
      url: `api/v2/products/cart/refetch`,
      method: 'POST',
      disableOnMount: true,
    },
  });
  const emptiedCartSyncedRef = useRef<number | null>(null);

  useEffect(() => {
    if (
      marketType !== 'EMARKET' ||
      farmProducts.length > 0 ||
      !cartId ||
      readPaymentInProgress() ||
      emptiedCartSyncedRef.current === cartId
    ) {
      return;
    }

    emptiedCartSyncedRef.current = cartId;
    syncEmptiedCart({
      farmProducts: [],
      priceTotal: 0,
      coupons: [],
      marketType: 'EMARKET',
      cartId,
    });
  }, [farmProducts.length, cartId, marketType]);

  const isLoading =
    isLoadingBanners ||
    isLoadingProducts ||
    isLoadingCategory ||
    isLoadingPopularCategories ||
    isLoadingProductsDiscount ||
    isLoadingProductsCount;

  useEffect(() => {
    const cartPaths: string[] = [
      PublicRoute.CART_PAGE,
      PublicRoute.WHOLESALE_CART,
    ];

    if (!cartPaths.includes(window.location.pathname)) {
      dispatchCart({
        type: CartActionType.REMOVE_ALL_COUPONS,
      });
    }
  }, [window.location.pathname]);

  return (
    <>
      {notificationHolder}
      <CookieConsent />
      <StyledPage>
        <Spinner spinning={isLoading} size={'large'}>
          <Layout className={'layout'}>
            <MobileHeader />
            <HomeInfoBar />
            <DefaultSecondHeader />
            <Content>
              <Router />
            </Content>
            <DefaultFooter />
            <MobileMenu farmProducts={farmProducts} />
          </Layout>
        </Spinner>
      </StyledPage>
    </>
  );
};

DefaultLayout.PageLayout = PageLayout;
DefaultLayout.PageContent = PageContent;
DefaultLayout.Banner = Banner;

export default DefaultLayout;
